/**
 * Trợ lý Agent RPA (`RpaAgentView`, `AgentTaskForm`, `AgentActionItem` của web-portal native): kiểm yêu cầu, danh sách
 * agent của Gateway, khi nào hỏi lại danh sách task, hành động nào thực thi được. Giới hạn chép từ
 * `modules/rpa/backend/src/agent/agent.ts`.
 */
import type { HanhDongAgent, LoaiHanhDongAgent, TaskAgent, TrangThaiAgent } from '../types.ts';

export const PHAM_VI_NGAY = [7, 14, 30] as const;
export const YEU_CAU_TOI_DA = 4000;
/** Agent mặc định khi Gateway chưa báo danh sách — khớp `RPA_AGENT_GATEWAY_MODEL` mặc định của máy chủ. */
export const AGENT_MAC_DINH = 'hermes';
const MODEL = /^[A-Za-z0-9._/-]{1,64}$/;
/** Hỏi lại danh sách khi còn task hoặc hành động đang chạy (native 4 giây). */
export const CHU_KY_TASK_MS = 4_000;

export const MAU_YEU_CAU = ['errors', 'overview', 'retry'] as const;
export type MauYeuCau = (typeof MAU_YEU_CAU)[number];

export function kiemYeuCau(prompt: string): 'batBuoc' | 'quaDai' | null {
  const s = prompt.trim();
  if (!s) return 'batBuoc';
  if (s.length > YEU_CAU_TOI_DA) return 'quaDai';
  return null;
}

/** Tên agent Gateway đang phục vụ: Gateway trả chuỗi hoặc object có `name`/`id`; bỏ tên không hợp lệ và trùng. */
export function danhSachAgent(tt: TrangThaiAgent | undefined): string[] {
  const ten = (tt?.backends ?? []).map((b) => {
    if (typeof b === 'string') return b;
    if (b && typeof b === 'object') {
      const o = b as Record<string, unknown>;
      return typeof o.name === 'string' ? o.name : typeof o.id === 'string' ? o.id : '';
    }
    return '';
  });
  const hop = [...new Set(ten.map((x) => x.trim()).filter((x) => MODEL.test(x)))];
  return hop.length ? hop : [AGENT_MAC_DINH];
}

export type TinhTrangGateway = 'chuaCauHinh' | 'khongKetNoi' | 'daKetNoi';

export function tinhTrangGateway(tt: TrangThaiAgent): TinhTrangGateway {
  if (!tt.configured) return 'chuaCauHinh';
  return tt.reachable ? 'daKetNoi' : 'khongKetNoi';
}

/** Còn task đang xử lý hoặc hành động đang thực thi thì hỏi lại; xong hết thì thôi. */
export function conDangChay(ds: readonly TaskAgent[] | undefined): boolean {
  return (ds ?? []).some((t) => t.status === 'running' || t.actions.some((a) => a.execution?.status === 'running'));
}

/** `review_workflow` chỉ để xem lại, không thực thi tự động. */
const THUC_THI_DUOC: readonly LoaiHanhDongAgent[] = ['retry_run', 'run_workflow', 'check_robot'];
/** Kiểm tra Robot chỉ đọc nên kiểm lại được; hai loại còn lại chạy Robot thật, chỉ một lần. */
const LAP_LAI_DUOC: readonly LoaiHanhDongAgent[] = ['check_robot'];

/** Hành động hiện nút thực thi: task đã hoàn tất, loại thực thi được, không đang chạy, chưa xong (trừ kiểm tra Robot). */
export function thucThiDuoc(task: Pick<TaskAgent, 'status'>, a: HanhDongAgent): boolean {
  const tt = a.execution?.status;
  return (
    task.status === 'completed' &&
    THUC_THI_DUOC.includes(a.type) &&
    tt !== 'running' &&
    (tt !== 'done' || LAP_LAI_DUOC.includes(a.type))
  );
}

/** Nhãn nút: thử lại sau lần lỗi, "Kiểm tra ngay" cho Robot, còn lại "Thực thi". */
export function nutThucThi(a: HanhDongAgent): 'thuLai' | 'kiemTra' | 'thucThi' {
  if (a.execution?.status === 'failed') return 'thuLai';
  return a.type === 'check_robot' ? 'kiemTra' : 'thucThi';
}

/** Màn nơi người dùng tự làm việc agent đề xuất. */
export const MAN_CUA_HANH_DONG: Record<LoaiHanhDongAgent, string> = {
  retry_run: '/rpa/history',
  run_workflow: '/rpa/projects',
  check_robot: '/rpa/opencore-users',
  review_workflow: '/rpa/incidents',
};

/** Project chọn sẵn: chỉ một Project chạy được thì chọn luôn. */
export function projectChonSan(projects: readonly { id: string }[], daChon: string | undefined): string | undefined {
  return daChon ?? (projects.length === 1 ? projects[0]?.id : undefined);
}
